import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";

import { APP_BASE } from "./api/base";
import { Claim } from "./routes/Claim";
import { Course } from "./routes/Course";
import { Alignment } from "./routes/Alignment";
import { Cards } from "./routes/Cards";
import { Dashboard } from "./routes/Dashboard";
import { Homework } from "./routes/Homework";
import { Readings } from "./routes/Readings";
import { LtiDeepLink } from "./routes/LtiDeepLink";
import { LtiLaunched } from "./routes/LtiLaunched";
import { Devices } from "./routes/Devices";
import { Home } from "./routes/Home";
import { Institutions } from "./routes/Institutions";
import { InviteAccept } from "./routes/InviteAccept";
import { Host } from "./routes/Host";
import { Items } from "./routes/Items";
import { Join } from "./routes/Join";
import { Kit } from "./routes/Kit";
import { Live } from "./routes/Live";
import { Me } from "./routes/Me";
import { MyClasses, MyProgress } from "./routes/My";
import { Pair } from "./routes/Pair";
import { LessonPlan } from "./routes/LessonPlan";
import { Plan } from "./routes/Plan";
import { Projector } from "./routes/Projector";
import { Settings } from "./routes/Settings";
import { SignIn } from "./routes/SignIn";
import { SignUp } from "./routes/SignUp";
import { TeacherSignUp } from "./routes/TeacherSignUp";
import { VerifyEmail } from "./routes/VerifyEmail";
import { Student } from "./routes/Student";
import { StudentDetail } from "./routes/StudentDetail";
import { Summary } from "./routes/Summary";
import { Transfer } from "./routes/Transfer";
import { Teacher } from "./routes/Teacher";

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home }),
  createRoute({ getParentRoute: () => rootRoute, path: "/kit", component: Kit }),
  createRoute({ getParentRoute: () => rootRoute, path: "/signin", component: SignIn }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/signup", component: TeacherSignUp }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/verify",
    component: VerifyEmail,
    validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === "string" ? s.token : undefined }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/j/$code", component: Join }),
  createRoute({ getParentRoute: () => rootRoute, path: "/j", component: Join }),
  createRoute({ getParentRoute: () => rootRoute, path: "/s/$sessionId", component: Student }),
  createRoute({ getParentRoute: () => rootRoute, path: "/me", component: Me }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pair", component: Pair }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/signup",
    component: SignUp,
    validateSearch: (s: Record<string, unknown>) => ({ session: typeof s.session === "string" ? s.session : undefined }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/my/classes", component: MyClasses }),
  createRoute({ getParentRoute: () => rootRoute, path: "/my/progress", component: MyProgress }),
  createRoute({ getParentRoute: () => rootRoute, path: "/my/transfer", component: Transfer }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/claim",
    component: Claim,
    validateSearch: (s: Record<string, unknown>) => ({ session: typeof s.session === "string" ? s.session : undefined }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t", component: Teacher }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/host", component: Host }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/settings", component: Settings }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/devices", component: Devices }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/institutions", component: Institutions }),
  createRoute({ getParentRoute: () => rootRoute, path: "/invite", component: InviteAccept }),
  createRoute({ getParentRoute: () => rootRoute, path: "/lti/launched", component: LtiLaunched }),
  createRoute({ getParentRoute: () => rootRoute, path: "/sso/landed", component: LtiLaunched }),
  createRoute({ getParentRoute: () => rootRoute, path: "/lti/deep-link", component: LtiDeepLink }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId", component: Course }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/items", component: Items }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/dashboard", component: Dashboard }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/alignment", component: Alignment }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/cards", component: Cards }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/homework", component: Homework }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/readings", component: Readings }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/students/$personId", component: StudentDetail }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/plan", component: Plan }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/c/$courseId/lessons/$lessonId", component: LessonPlan }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/live/$sessionId", component: Live }),
  createRoute({ getParentRoute: () => rootRoute, path: "/t/live/$sessionId/summary", component: Summary }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/p/$sessionId",
    component: Projector,
    validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === "string" ? s.token : undefined, code: typeof s.code === "string" ? s.code : undefined }),
  }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({ routeTree, basepath: APP_BASE || undefined });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
